/**
 * Workflow Service
 * Handles workflow-related API calls
 */

import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { withBuScope } from '@/lib/business-unit-scope';
import { WorkflowAdapter } from '@/adapters';
import type {
  ApiResponse,
  Workflow,
  WorkflowSummary,
  PaginationMeta,
  CreateWorkflowDto,
  UpdateWorkflowDto,
  PatchSEGNodeDto,
  WorkflowResponseDto,
  WorkflowsListResponseDto,
  WorkflowType,
  CompilationStatus,
  VectorSyncResult
} from '@/types';

export interface WorkflowCategoryTree {
  categories: { category: string; count: number; subcategories: { name: string; count: number }[] }[];
  uncategorized: number;
}

/** An AI edit proposal for a protocol's document; nothing is saved until the editor saves it. */
export interface AiEditProposal {
  proposedDoc: Record<string, unknown>;
  summary: string;
  baseEditorDataUpdatedAt: string | null;
}

export type AiReviewFocus = 'audit' | 'improve';

/** One thing an AI review found: from the deterministic checks, or from the model. */
export interface AiReviewFinding {
  id: string;
  source: 'check' | 'ai';
  severity: 'high' | 'medium' | 'low';
  title: string;
  detail: string;
  where?: { section: string; subProtocolTitle?: string; lineText?: string };
  /** An instruction the AI edit can run to fix it. */
  suggestedInstruction?: string;
  /** Only a person can fix it: a value the protocol needs (who to email, which agent). */
  needsValue?: boolean;
}

export interface AiReview {
  findings: AiReviewFinding[];
  /** The model part failed; only the checks' findings are here. */
  partial?: boolean;
}

type WorkflowListFilters = {
  status?: boolean;
  type?: WorkflowType;
  compilationStatus?: CompilationStatus;
  page?: number;
  limit?: number;
  search?: string;
  tool?: string;
  provider?: string;
  category?: string;
  subcategory?: string;
  /** Only workflows with no category yet. */
  uncategorized?: boolean;
};

export const workflowService = {
  /**
   * Get paginated workflows for the current user's account
   * Returns lightweight WorkflowSummary (no seg, editorData, instructions, expressions)
   */
  async getWorkflows(
    filters?: WorkflowListFilters,
    scope?: { bu_id?: string }
  ): Promise<{ workflows: WorkflowSummary[]; pagination: PaginationMeta }> {
    const query = new URLSearchParams();
    if (scope?.bu_id) query.set('bu_id', scope.bu_id);
    if (filters?.status !== undefined) query.set('status', String(filters.status));
    if (filters?.type) query.set('type', filters.type);
    if (filters?.page !== undefined) query.set('page', String(filters.page));
    if (filters?.limit !== undefined) query.set('limit', String(filters.limit));
    if (filters?.search?.trim()) query.set('search', filters.search.trim());
    if (filters?.compilationStatus) query.set('compilationStatus', filters.compilationStatus);
    if (filters?.tool) query.set('tool', filters.tool);
    if (filters?.provider) query.set('provider', filters.provider);
    if (filters?.uncategorized) query.set('uncategorized', 'true');
    if (filters?.category) query.set('category', filters.category);
    if (filters?.subcategory) query.set('subcategory', filters.subcategory);

    const url = query.size > 0 ? `${API_ENDPOINTS.WORKFLOWS.BASE}?${query.toString()}` : API_ENDPOINTS.WORKFLOWS.BASE;
    const response = await api.get<ApiResponse<WorkflowsListResponseDto>>(url);
    return WorkflowAdapter.fromSummaryListApi(response.data.data);
  },

  /**
   * Get a single workflow by ID
   * @param id - Workflow MongoDB ObjectId
   * @returns Promise resolving to workflow
   */
  async getWorkflowById(id: string): Promise<Workflow> {
    const response = await api.get<ApiResponse<WorkflowResponseDto>>(API_ENDPOINTS.WORKFLOWS.BY_ID(id));
    return WorkflowAdapter.fromApi(response.data.data);
  },

  /**
   * Create a new workflow
   * @param data - Workflow creation data
   * @returns Promise resolving to created workflow
   */
  async createWorkflow(data: CreateWorkflowDto, scope: { bu_id?: string } = {}): Promise<Workflow> {
    const payload = WorkflowAdapter.toCreateRequest(data);
    const response = await api.post<ApiResponse<WorkflowResponseDto>>(
      withBuScope(API_ENDPOINTS.WORKFLOWS.BASE, scope),
      payload
    );
    return WorkflowAdapter.fromApi(response.data.data);
  },

  /**
   * Update an existing workflow (partial update)
   * @param id - Workflow MongoDB ObjectId
   * @param data - Partial workflow update data
   * @returns Promise resolving to updated workflow
   */
  async updateWorkflow(id: string, data: UpdateWorkflowDto): Promise<Workflow> {
    const payload = WorkflowAdapter.toUpdateRequest(data);
    const response = await api.put<ApiResponse<WorkflowResponseDto>>(API_ENDPOINTS.WORKFLOWS.BY_ID(id), payload);
    return WorkflowAdapter.fromApi(response.data.data);
  },

  /**
   * Delete a workflow
   * @param id - Workflow MongoDB ObjectId
   * @returns Promise resolving when deletion is complete
   */
  async deleteWorkflow(id: string): Promise<void> {
    await api.delete<ApiResponse<void>>(API_ENDPOINTS.WORKFLOWS.BY_ID(id));
  },

  /**
   * Toggle workflow status (enabled/disabled)
   * @param id - Workflow MongoDB ObjectId
   * @returns Promise resolving to updated workflow
   */
  async toggleWorkflowStatus(id: string): Promise<Workflow> {
    const response = await api.put<ApiResponse<WorkflowResponseDto>>(API_ENDPOINTS.WORKFLOWS.TOGGLE(id));
    return WorkflowAdapter.fromApi(response.data.data);
  },

  /**
   * Update channels for workflow
   * @param id - Workflow MongoDB ObjectId
   * @param enabledChannels - Array of channel IDs to enable
   * @returns Promise resolving to updated workflow
   */
  async updateWorkflowChannels(id: string, enabledChannels: string[]): Promise<Workflow> {
    const payload = WorkflowAdapter.toUpdateChannelsRequest(enabledChannels);
    const response = await api.put<ApiResponse<WorkflowResponseDto>>(API_ENDPOINTS.WORKFLOWS.CHANNELS(id), payload);
    return WorkflowAdapter.fromApi(response.data.data);
  },

  /**
   * Patch a single node in the workflow SEG by node id (edges and other nodes unchanged)
   */
  async patchSEGNode(workflowId: string, body: PatchSEGNodeDto): Promise<Workflow> {
    const response = await api.patch<ApiResponse<WorkflowResponseDto>>(API_ENDPOINTS.WORKFLOWS.SEG_NODE(workflowId), body);
    return WorkflowAdapter.fromApi(response.data.data);
  },

  /**
   * Trigger Pinecone re-sync for a single workflow
   */
  async resyncWorkflow(id: string, dryRun = false): Promise<VectorSyncResult> {
    const response = await api.post<ApiResponse<VectorSyncResult>>(
      API_ENDPOINTS.WORKFLOWS.RESYNC(id),
      {},
      { params: dryRun ? { dryRun: true } : undefined }
    );
    return response.data.data;
  },

  /** Stored categories → subcategories with counts, for the category filter. */
  async getCategoryTree(type: WorkflowType | undefined, scope: { bu_id?: string } = {}): Promise<WorkflowCategoryTree> {
    const url = withBuScope(API_ENDPOINTS.WORKFLOWS.CATEGORY_TREE, scope);
    const response = await api.get<ApiResponse<WorkflowCategoryTree>>(url, { params: type ? { type } : undefined });
    return response.data.data;
  },

  /** File every uncategorized workflow under a category from the intent catalog. */
  async autoCategorize(scope: { bu_id?: string } = {}): Promise<{ scanned: number; categorized: number }> {
    const response = await api.post<ApiResponse<{ scanned: number; categorized: number }>>(
      withBuScope(API_ENDPOINTS.WORKFLOWS.AUTO_CATEGORIZE, scope),
      {},
      // One model call per item, a batch of up to 50 — longer than the default timeout.
      { timeout: 5 * 60 * 1000 }
    );
    return response.data.data;
  },

  /** The workflow's text format — what its linked SOP holds and search/backtests read. */
  async getWorkflowText(id: string): Promise<string> {
    const response = await api.get<ApiResponse<{ markdown: string }>>(API_ENDPOINTS.WORKFLOWS.TEXT(id));
    return response.data.data.markdown;
  },

  /** Start (or attach to) a protocol's guided compile; later questions arrive over the socket. */
  async compileGuided(id: string): Promise<{ sessionId: string; sopId: string; status: string; question: unknown }> {
    // The call returns at the compile's first question, which can take a few minutes to reach.
    const response = await api.post<ApiResponse<{ sessionId: string; sopId: string; status: string; question: unknown }>>(
      API_ENDPOINTS.WORKFLOWS.COMPILE_GUIDED(id),
      {},
      { timeout: 5 * 60 * 1000 }
    );
    return response.data.data;
  },

  /** Draft a protocol from a plain-language description. */
  async generateProtocol(prompt: string, scope: { bu_id?: string } = {}): Promise<Workflow> {
    const response = await api.post<ApiResponse<WorkflowResponseDto>>(
      withBuScope(API_ENDPOINTS.WORKFLOWS.GENERATE_PROTOCOL, scope),
      { prompt },
      // Drafting runs a model call and a repair round; the default timeout would cut it off.
      { timeout: 240_000 }
    );
    return WorkflowAdapter.fromApi(response.data.data);
  },

  /**
   * Propose an edit to a protocol's document from an instruction. `baseEditorDataUpdatedAt` is the
   * value loaded with the protocol, sent back as-is (409 when someone saved since). Saves nothing.
   */
  async aiEditWorkflow(
    id: string,
    body: { doc: Record<string, unknown>; instruction: string; baseEditorDataUpdatedAt: string | null },
    scope: { bu_id?: string } = {}
  ): Promise<AiEditProposal> {
    const response = await api.post<ApiResponse<AiEditProposal>>(withBuScope(API_ENDPOINTS.WORKFLOWS.AI_EDIT(id), scope), body, {
      // A model call and a repair round, like drafting a protocol.
      timeout: 240_000
    });
    return response.data.data;
  },

  /** Review a protocol's document without changing it: `audit` finds problems, `improve` suggests changes. */
  async aiReviewWorkflow(
    id: string,
    body: { doc: Record<string, unknown>; focus: AiReviewFocus },
    scope: { bu_id?: string } = {}
  ): Promise<AiReview> {
    const response = await api.post<ApiResponse<AiReview>>(withBuScope(API_ENDPOINTS.WORKFLOWS.AI_REVIEW(id), scope), body, {
      timeout: 120_000
    });
    return response.data.data;
  }
};
